import React, { useMemo, useContext } from 'react';
import { AuxFrame, AuxFrameContext } from '../AuxFrame/AuxFrame';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { AuxFramePhotoSlider } from '../AuxFrame/components/AuxFramePhotoSlider/AuxFramePhotoSlider';
import { Box } from '@withjoy/joykit';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { isInIframe } from '@shared/utils/isInIframe';
import { EventPageType } from '@graphql/generated-base';
import { EditPhoto } from '../AuxFrame/components/EditPhoto';
import { AuxFrameTitle } from '../AuxFrame/components/AuxFrameTitle/AuxFrameTitle';
import { useTranslation } from '@shared/core';

export interface LegacyHeaderProps {
  title?: Maybe<string>;
  subtitle?: Maybe<string>;
  eventHandle: string;
  telemetryPhotoEditor: {
    onEditPhotoOpenedChange: (opened: boolean) => void;
  };
  children: React.ReactNode;
}

export const LegacyHeader: React.FC<LegacyHeaderProps> = ({ title, subtitle, telemetryPhotoEditor, children }) => {
  const { photo, pageId, page, pages, pageSlug } = useContext(AuxFrameContext);
  const isPreviewing = isInIframe();
  const { onEditPhotoOpenedChange } = telemetryPhotoEditor;
  const { t } = useTranslation('joykit');
  const inlineEditorTransFn = t('inlineEditor');

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editPhoto', inlineEditData: { photoPresentation: null, enablePhotoLayout: false, photo, pageId, page, pageSlug } },
    });
  };

  const welcomePageId = useMemo(() => pages.find(page => page.type === EventPageType.welcome)?.id || '', [pages]);

  return (
    <>
      <AuxFrame>
        <InlineEditor
          elementLabel={inlineEditorTransFn.photo()}
          wrapperType="actionInside"
          actionData={{
            editPhoto: handleEditPhotoClick,
          }}
          minHeight="100vh"
          componentName="pagePhoto"
          pageName={page}
          pageSlug={pageSlug || ''}
          isEligibleForInlineEditing={page !== EventPageType.app}
        >
          {photo?.url ? (
            <AuxFramePhotoSlider photo={photo} />
          ) : (
            <Box
              width="100%"
              height="100vh"
            />
          )}
        </InlineEditor>
        <AuxFrameTitle
          x={'left'}
          y={'bottom'}
          textLight={true}
          title={title}
          subtitle={subtitle}
          welcomePageId={welcomePageId}
          page={page}
          pageSlug={pageSlug || ''}
        />
        {children}
        {!isPreviewing && page !== EventPageType.app && <EditPhoto onEditPhotoOpenedChange={onEditPhotoOpenedChange} />}
      </AuxFrame>
    </>
  );
};
